import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  Save,
  User,
  Mail,
  Calendar,
  Shield,
  Key,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  Sparkles,
  Crown,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Profile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setProfile(response.data);
      setFormData((prev) => ({
        ...prev,
        name: response.data.name || "",
      }));
      setLoading(false);
    } catch (error) {
      toast.error("Failed to load profile");
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();

    // Validate password if changing
    if (formData.new_password) {
      if (!formData.current_password) {
        toast.error("Please enter your current password");
        return;
      }
      if (formData.new_password !== formData.confirm_password) {
        toast.error("New passwords do not match");
        return;
      }
      if (formData.new_password.length < 6) {
        toast.error("New password must be at least 6 characters");
        return;
      }
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const updateData = {
        name: formData.name,
      };

      if (formData.new_password) {
        updateData.current_password = formData.current_password;
        updateData.new_password = formData.new_password;
      }

      await axios.put(`${API}/users/me`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Update localStorage
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      user.name = formData.name;
      localStorage.setItem("user", JSON.stringify(user));

      toast.success("Profile updated successfully!");

      // Clear password fields
      setFormData((prev) => ({
        ...prev,
        current_password: "",
        new_password: "",
        confirm_password: "",
      }));

      // Reload to refresh header
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const isSubscriptionActive = () => {
    if (!profile?.subscription_valid_until) return false;
    try {
      return new Date(profile.subscription_valid_until) > new Date();
    } catch {
      return false;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <p className="text-gray-500 text-sm">
            Loading profile information...
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-4" data-testid="profile-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold bg-gradient-to-r from-primary-500 to-orange-500 bg-clip-text text-transparent animate-gradient">
              My Profile
            </h1>
            <p className="text-gray-500 text-xs mt-0.5">
              View and update your account information
            </p>
          </div>
          <div className="flex gap-2">
            <Badge className="text-xs py-0.5 px-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-sm">
              <Sparkles size={10} className="mr-1" />
              Verified
            </Badge>
            <Button
              variant="outline"
              onClick={() => navigate("/")}
              className="h-7 text-xs border-gray-300 hover:border-gray-400"
            >
              <ArrowLeft size={10} className="mr-1" />
              Dashboard
            </Button>
          </div>
        </div>

        {/* Account Info Card */}
        <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
          <CardHeader className="pb-3 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-gradient-to-br from-primary-400 to-primary-500">
                    <User size={14} className="text-white" />
                  </div>
                  Account Information
                </CardTitle>
                <CardDescription className="text-xs text-gray-500">
                  Your account details and subscription status
                </CardDescription>
              </div>
              {profile?.role === "admin" && (
                <Badge className="text-xs bg-gradient-to-r from-orange-500 to-amber-500 text-white border-0">
                  <Crown size={10} className="mr-1" />
                  Admin
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="p-1 rounded-md bg-gradient-to-br from-gray-100 to-gray-50">
                    <Mail size={12} className="text-gray-600" />
                  </div>
                  <Label className="text-xs text-gray-500">Email Address</Label>
                </div>
                <div className="text-sm font-medium text-gray-800 truncate">
                  {profile?.email}
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="p-1 rounded-md bg-gradient-to-br from-gray-100 to-gray-50">
                    <Shield size={12} className="text-gray-600" />
                  </div>
                  <Label className="text-xs text-gray-500">Account Role</Label>
                </div>
                <Badge
                  className={`text-xs px-2 py-0.5 ${
                    profile?.role === "admin"
                      ? "bg-gradient-to-br from-orange-500 to-amber-500 text-white border-0"
                      : "bg-gradient-to-br from-gray-500 to-gray-600 text-white border-0"
                  }`}
                >
                  {profile?.role === "admin"
                    ? "Administrator"
                    : "Standard User"}
                </Badge>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="p-1 rounded-md bg-gradient-to-br from-gray-100 to-gray-50">
                    <Calendar size={12} className="text-gray-600" />
                  </div>
                  <Label className="text-xs text-gray-500">Member Since</Label>
                </div>
                <div className="text-sm font-medium text-gray-800">
                  {formatDate(profile?.created_at)}
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="p-1 rounded-md bg-gradient-to-br from-gray-100 to-gray-50">
                    <Crown size={12} className="text-gray-600" />
                  </div>
                  <Label className="text-xs text-gray-500">Subscription</Label>
                </div>
                <div className="flex items-center gap-1.5">
                  {profile?.subscription_valid_until ? (
                    isSubscriptionActive() ? (
                      <>
                        <CheckCircle size={14} className="text-green-500" />
                        <div className="text-sm font-medium text-green-600">
                          Active
                        </div>
                      </>
                    ) : (
                      <>
                        <XCircle size={14} className="text-red-500" />
                        <div className="text-sm font-medium text-red-600">
                          Expired
                        </div>
                      </>
                    )
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                      <span className="text-sm text-gray-600">Free Plan</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Edit Profile Form */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Name Update */}
          <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
            <CardHeader className="pb-3 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-gradient-to-br from-primary-400 to-primary-500">
                      <User size={14} className="text-white" />
                    </div>
                    Update Name
                  </CardTitle>
                  <CardDescription className="text-xs text-gray-500">
                    Change your display name
                  </CardDescription>
                </div>
                <Badge variant="outline" className="text-xs bg-white">
                  Personal
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <form onSubmit={handleUpdateProfile}>
                <div className="space-y-2">
                  <Label
                    htmlFor="name"
                    className="text-xs font-medium text-gray-700"
                  >
                    Display Name
                  </Label>
                  <div className="relative group">
                    <Input
                      id="name"
                      data-testid="name-input"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="Your full name"
                      className="h-8 text-xs pl-9 border-gray-300 focus:border-primary-400 focus:ring-1 focus:ring-primary-400/20 group-hover:border-primary-300"
                    />
                    <User
                      size={14}
                      className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover:text-primary-500"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={
                    saving ||
                    !formData.name.trim() ||
                    formData.name === profile?.name
                  }
                  className="w-full mt-4 h-8 text-xs bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 text-white shadow-sm"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1.5"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={12} className="mr-1.5" />
                      Save Name
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Password Change */}
          <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
            <CardHeader className="pb-3 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-gradient-to-br from-primary-400 to-primary-500">
                      <Key size={14} className="text-white" />
                    </div>
                    Change Password
                  </CardTitle>
                  <CardDescription className="text-xs text-gray-500">
                    Update your account password
                  </CardDescription>
                </div>
                <Badge variant="outline" className="text-xs bg-white">
                  Security
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label
                    htmlFor="current_password"
                    className="text-xs font-medium text-gray-700"
                  >
                    Current Password
                  </Label>
                  <div className="relative group">
                    <Input
                      id="current_password"
                      data-testid="current-password-input"
                      type={showCurrentPassword ? "text" : "password"}
                      value={formData.current_password}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          current_password: e.target.value,
                        })
                      }
                      placeholder="Enter your current password"
                      className="h-8 text-xs pl-9 pr-9 border-gray-300 focus:border-primary-400 focus:ring-1 focus:ring-primary-400/20 group-hover:border-primary-300"
                    />
                    <Key
                      size={14}
                      className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover:text-primary-500"
                    />
                    <button
                      type="button"
                      className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() =>
                        setShowCurrentPassword(!showCurrentPassword)
                      }
                    >
                      {showCurrentPassword ? (
                        <EyeOff size={14} />
                      ) : (
                        <Eye size={14} />
                      )}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="new_password"
                      className="text-xs font-medium text-gray-700"
                    >
                      New Password
                    </Label>
                    <div className="relative group">
                      <Input
                        id="new_password"
                        data-testid="new-password-input"
                        type={showNewPassword ? "text" : "password"}
                        value={formData.new_password}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            new_password: e.target.value,
                          })
                        }
                        placeholder="Minimum 6 characters"
                        className="h-8 text-xs pl-9 pr-9 border-gray-300 focus:border-primary-400 focus:ring-1 focus:ring-primary-400/20 group-hover:border-primary-300"
                      />
                      <Key
                        size={14}
                        className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover:text-primary-500"
                      />
                      <button
                        type="button"
                        className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? (
                          <EyeOff size={14} />
                        ) : (
                          <Eye size={14} />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label
                      htmlFor="confirm_password"
                      className="text-xs font-medium text-gray-700"
                    >
                      Confirm Password
                    </Label>
                    <div className="relative group">
                      <Input
                        id="confirm_password"
                        data-testid="confirm-password-input"
                        type="password"
                        value={formData.confirm_password}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            confirm_password: e.target.value,
                          })
                        }
                        placeholder="Re-enter new password"
                        className="h-8 text-xs pl-9 border-gray-300 focus:border-primary-400 focus:ring-1 focus:ring-primary-400/20 group-hover:border-primary-300"
                      />
                      <Key
                        size={14}
                        className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover:text-primary-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-2">
                  <Button
                    onClick={handleUpdateProfile}
                    disabled={
                      saving ||
                      !formData.current_password ||
                      !formData.new_password ||
                      formData.new_password !== formData.confirm_password ||
                      formData.new_password.length < 6
                    }
                    className="w-full h-8 text-xs bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 text-white shadow-sm"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1.5"></div>
                        Updating...
                      </>
                    ) : (
                      <>
                        <Save size={12} className="mr-1.5" />
                        Update Password
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Admin Notice */}
        {profile?.role === "admin" && (
          <Alert className="border-0 bg-gradient-to-r from-orange-50/80 to-amber-50/60 border border-orange-200/70 backdrop-blur-sm">
            <div className="flex items-start gap-2.5">
              <div className="p-1.5 rounded-md bg-gradient-to-br from-orange-500 to-amber-500">
                <Crown size={14} className="text-white" />
              </div>
              <div>
                <AlertTitle className="text-sm font-bold text-gray-800">
                  Administrator Access
                </AlertTitle>
                <AlertDescription className="text-xs text-gray-600 mt-1">
                  You have elevated permissions to manage users and
                  subscriptions.
                  <div className="mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate("/admin")}
                      className="h-6 text-xs border-orange-200 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                    >
                      Access Admin Panel
                    </Button>
                  </div>
                </AlertDescription>
              </div>
            </div>
          </Alert>
        )}

        {/* Quick Tips */}
        <div className="relative overflow-hidden rounded-lg border border-primary-200/50 bg-gradient-to-br from-primary-50/30 to-orange-50/20">
          <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-primary-500 to-orange-400 opacity-40 rounded-bl-full"></div>

          <div className="p-4 relative">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-sm font-semibold bg-gradient-to-r from-primary-600 to-orange-600 bg-clip-text text-transparent">
                    Account Security Tips
                  </h3>
                  <Badge
                    variant="outline"
                    className="text-[10px] py-0 px-1.5 border-primary-200 text-primary-600"
                  >
                    Security
                  </Badge>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    {
                      tip: "Use a strong password with mixed characters",

                      color: "from-blue-500 to-cyan-500",
                    },
                    {
                      tip: "Update password regularly for security",
                      icon: "🔄",
                      color: "from-green-500 to-emerald-500",
                    },
                    {
                      tip: "Never share your password with anyone",

                      color: "from-red-500 to-orange-500",
                    },
                    {
                      tip: "Log out when using shared devices",

                      color: "from-purple-500 to-pink-500",
                    },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 p-2 rounded-md bg-white/50 border border-gray-200/50 hover:bg-white hover:border-primary-200/70 transition-all duration-200"
                    >
                      <p className="text-xs text-gray-700 flex-1">{item.tip}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
